import { PolymerElement } from '@polymer/polymer/polymer-element.js';
import '@polymer/polymer/lib/elements/dom-bind.js';
import '@polymer/iron-input/iron-input.js';
import '@polymer/iron-icon/iron-icon.js';
import '@polymer/paper-input/paper-input-container.js';
import '@polymer/paper-input/paper-input-error.js';
import '@polymer/paper-item/paper-item.js';
import '@polymer/paper-item/paper-icon-item.js';
import '@polymer/neon-animation/neon-animation.js';
import '@polymer/iron-collapse/iron-collapse.js';
import '../bind-path/bind-path.js';
import '../katapult-elements/katapult-button.js';
import { KatapultDialog } from '../katapult-elements/katapult-dialog.js';
import { html } from '@polymer/polymer/lib/utils/html-tag.js';
import { uuidv4 } from '../../modules/browserUUIDv4.js';
import firebase from 'firebase/compat/app';
import 'firebase/compat/storage';
import { logger } from '@sentry/browser';

class FileInput extends PolymerElement {
  static get template() {
    return html`
      <style>
        :host {
          flex-grow: 1;
          color: var(--primary-text-color);
          --file-input-font-size: 12pt;
          min-width: 0;
        }
        :host([_dragover]) #container {
          background-color: rgba(255, 255, 255, 0.1);
        }
        :host([has-error]) #addFileButton {
          margin-top: 18px;
        }
        #addFileButton {
          display: flex;
        }
        #container {
          padding: 8px 0;
          --paper-input-container-label: {
            font-size: var(--file-input-font-size);
          };
          --paper-input-container-disabled: {
            pointer-events: auto;
            opacity: 1;
          };
          transition: background-color 0.1s;
          border-radius: 4px;
        }
        #placeholder {
          color: var(--secondary-text-color);
        }
        .filename {
          display: flex;
          justify-content: flex-start;
          align-items: center;
          font-size: var(--file-input-font-size);
          color: var(--primary-text-color);
          @apply (--file-input-filename);
          padding: 2px;
          padding-left: 8px;
          border-radius: 2px;
        }
        .filename:hover {
          background-color: rgba(255, 255, 255, 0.1);
        }
        .filename > span {
          flex-grow: 1;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          margin-right: 4px;
        }
      </style>
      <katapult-firebase-worker id="files" path="[[path]]" data="{{_files}}" data-as-array></katapult-firebase-worker>
      <paper-input-container id="container" always-float-label="[[files.length]]" invalid="[[invalid]]" disabled$="[[disabled]]">
        <!-- <div slot="prefix"><iron-icon icon="add"></iron-icon></div> -->
        <label slot="label">[[label]]</label>
        <iron-input slot="input">
          <!-- Firebase Files -->
          <template is="dom-repeat" items="[[files]]" as="file">
            <dom-bind
              disabled="[[disabled]]"
              file="[[file]]"
              add-only="[[addOnly]]"
              read-only="[[readOnly]]"
              _bound-download-file="[[_boundDownloadFile]]"
              _bound-open-file="[[_boundOpenFile]]"
              _downloading="[[_downloading]]"
              _or="[[_or]]"
              _show-delete="[[_showDelete]]"
              _show-open-in-window="[[_showOpenInWindow]]"
              _prompt-delete-file="[[_promptDeleteFile]]"
              _delete-file="[[_boundDeleteFile]]"
            >
              <template>
                <bind-path base="[[_downloading]]" path="[[file.$key]]" value="{{downloading}}"></bind-path>
                <div class="filename">
                  <span title="[[file.name]]">[[file.name]]</span>
                  <div style="display:flex; flex-direction:row; justify-content:flex-start; align-items:center;">
                    <template is="dom-if" if="[[_showOpenInWindow(file)]]">
                      <katapult-button
                        key="[[file.$key]]"
                        size="18"
                        icon="open_in_new"
                        iconOnly
                        noBorder
                        on-click="_boundOpenFile"
                        textColor="var(--primary-text-color)"
                      ></katapult-button>
                    </template>
                    <katapult-button
                      key="[[file.$key]]"
                      size="18"
                      icon="file_download"
                      iconOnly
                      noBorder
                      loading="[[_or(file.uploading, downloading)]]"
                      on-click="_boundDownloadFile"
                      textColor="var(--primary-text-color)"
                    ></katapult-button>
                    <template is="dom-if" if="[[_showDelete(addOnly, disabled, readOnly)]]">
                      <katapult-button
                        size="18"
                        icon="delete_forever"
                        iconOnly
                        noBorder
                        slot="dropdown-trigger"
                        textColor="var(--primary-text-color)"
                        on-click="_promptDeleteFile"
                      ></katapult-button>
                    </template>
                  </div>
                </div>
              </template>
            </dom-bind>
          </template>
          <input id="input" type="file" accept$="[[accept]]" style="display: none;" on-change="_inputChanged" multiple$="[[multiple]]" />
        </iron-input>
        <paper-input-error slot="add-on">[[error]]</paper-input-error>
        <template is="dom-if" if="[[_showAddFile(readOnly, disabled)]]">
          <katapult-button size="18" icon="upload" color="var(--secondary-color)" slot="add-on" id="addFileButton" on-click="_addFile"
            >[[placeholder]]</katapult-button
          >
        </template>
      </paper-input-container>
    `;
  }

  static get is() {
    return 'file-input';
  }
  static get properties() {
    return {
      accept: {
        type: String
      },
      addOnly: {
        type: Boolean,
        value: false
      },
      disabled: {
        type: Boolean,
        value: false
      },
      files: {
        type: Array,
        value: () => [],
        notify: true
      },
      _files: {
        type: Array,
        value: () => []
      },
      hasError: {
        type: Boolean,
        computed: '_calcHasError(invalid, error)',
        reflectToAttribute: true
      },
      invalid: {
        type: Boolean,
        value: false,
        notify: true
      },
      label: {
        type: String
      },
      placeholder: {
        type: String,
        value: 'Add File'
      },
      storagePath: {
        type: String,
        value: 'files'
      },
      multiple: {
        type: Boolean,
        value: false
      },
      uploadAutomatically: {
        type: Boolean,
        value: false
      },
      _uploadTasks: {
        type: Object,
        value: () => ({})
      },
      _downloading: {
        type: Object,
        value: () => ({})
      },
      _dragover: {
        type: Boolean,
        value: false,
        reflectToAttribute: true,
        observer: '_dragoverChanged'
      },
      _queue: {
        type: Object,
        value: () => ({})
      },
      readOnly: {
        type: Boolean
      },
      uploadAsPublic: {
        type: Boolean,
        value: false
      }
    };
  }
  static get observers() {
    return ['_updateFiles(_files.*)'];
  }
  constructor() {
    super();

    /**
     * The path in the Firebase database where file metadata and upload status is stored
     * @type {string}
     */
    this.path = '';
    /**
     * The path to the directory where a file is or will be stored
     * @type {string}
     */
    this.storagePath = 'files';
    /**
     * If true, a custom metadata `public_read` property will be added to any files uploaded with this input
     * @type {boolean}
     */
    this.uploadAsPublic = false;
  }
  ready() {
    this.addEventListener('dragover', (e) => {
      e.preventDefault();
      this._dragover = true;
    });
    this.addEventListener('dragleave', (e) => (this._dragover = false));
    this.addEventListener('drop', (e) => {
      e.preventDefault();
      this._dragover = false;
      this._filesDropped(e);
    });
    super.ready();
    this._boundDownloadFile = this._downloadFile.bind(this);
    this._boundOpenFile = this._openFile.bind(this);
    this._boundDeleteFile = this._deleteFile.bind(this);
  }
  checkForDuplicates(keys) {
    keys = keys || Object.keys(this._queue);
    if (!Array.isArray(keys)) keys = [keys];
    keys.forEach((key) => {
      // keep track number of potential duplicates
      let duplicates = 0;
      let file = this.get(`_queue.${key}`);
      for (let id in this.files) {
        let name = this.files[id].name;
        let extension = name.substring(name.indexOf('.') + 1);
        let reg = new RegExp(`\\s*\\([0-9]+\\)\\s*\\.${extension}`);
        // complicated bit to see figure out renaming of files if there are duplicates
        // probably just needs a better regex expression to fix get rid of if and else if
        if (duplicates == 0 && name == file.name && this.files[id].uploaded) duplicates++;
        else if (duplicates > 0 && name.replace(reg, '') == file.name.substring(0, file.name.indexOf('.')) && this.files[id].uploaded)
          duplicates++;
      }
      // if we don't have any duplicate file go ahead and upload it
      if (duplicates == 0) this.upload(key, file);
      else {
        // adjust the file name
        var newName = file.name.substring(0, file.name.indexOf('.')) + ` (${duplicates})` + file.name.substring(file.name.indexOf('.'));
        FirebaseWorker.ref(`${this.path}/${key}`).update({ name: newName });
        this.upload(key, file);
      }
    });
  }
  upload(key, file) {
    let reader = new FileReader();
    FirebaseWorker.ref(`${this.path}/${key}/uploading`).set(true);
    reader.onload = () => {
      this._uploadFile(key)
        .then(async (success) => {
          await globalThis.FirebaseWorker.database().ref(`${this.path}/${key}`).update({ uploading: false, uploaded: true });
          this.dispatchEvent(new CustomEvent('file-upload-completed'));
        })
        .catch((err) => {
          if (err.code !== 'storage/canceled') FirebaseWorker.ref(`${this.path}/${key}`).update({ uploading: false, uploaded: false });
        });
    };
    reader.readAsArrayBuffer(file);
  }
  _addFile(e) {
    this.error = '';
    this.invalid = false;
    this.$.input.click();
    this.blur();
  }
  _addFiles(files) {
    this.error = '';
    this.invalid = false;
    if (this.readOnly) {
      this.error = 'You do not have permission to upload files';
      this.invalid = true;
      return;
    }
    if (files && files.length) {
      if (!Array.isArray(files)) files = Array.from(files);
      if (!this.multiple) files = files.slice(0, 1);
      let keys = [];
      let acceptedTypes = (this.accept || '').split(',').filter((type) => type.startsWith('.'));
      let allAccepted = files.every((file) => {
        let accepted = !acceptedTypes.length || acceptedTypes.some((type) => file.name.endsWith(type));
        if (accepted) {
          let key = uuidv4();
          FirebaseWorker.ref(`${this.path}/${key}`).set({ name: file.name, type: file.type, uploading: false, uploaded: false });
          this.set(`_queue.${key}`, file);
          keys.push(key);
          return true;
        }
      });
      if (!allAccepted) {
        this.error = `Only ${acceptedTypes.join(', ')} files are allowed`;
        this.invalid = true;
      }
      this.checkForDuplicates(keys);
    }
  }
  _calcHasError(invalid, error) {
    return invalid == true && error != null;
  }
  async _deleteFile(key) {
    // Return if we don't have all the required data
    if (!key || !this.path) return;
    // Get abort controller.
    let uploadTask = this.get(`_uploadTasks.${key}`);
    let temp = null;
    let canceledUpload = false;
    if (uploadTask && (uploadTask.snapshot.state == 'running' || uploadTask.snapshot.state == 'paused')) {
      canceledUpload = uploadTask.cancel();
    }
    if (!canceledUpload) {
      try {
        await firebase.storage().ref(`${this.storagePath}/${key}`).delete();
      } catch (e) {
        console.error(e);
      }
    }
    // Delete file entry in firebase.
    await FirebaseWorker.ref(`${this.path}/${key}`).remove();
    this.dispatchEvent(new CustomEvent('file-deleted'));
  }
  _downloadFile(e) {
    let key = e.target.key;
    this.set(`_downloading.${key}`, true);
    this._openFile(e, true).then(() => {
      if (this.getAttribute('label') === 'Attacher Agreement') {
        // Track agreement file downloads
        logger.info('Downloaded attacher agreement file');
      }
      this.set(`_downloading.${key}`, false);
    });
  }
  _dragoverChanged(_dragover) {
    this.$.container._setFocused(_dragover);
  }
  _filesDropped(e) {
    this._addFiles(e.dataTransfer.files);
    this.$.input.value = null;
  }
  async _getFile(key) {
    // Make sure key exists.
    if (!key) throw 'Missing file key';
    // Get a download url from firebase.
    let url = await firebase.storage().ref(`${this.storagePath}/${key}`).getDownloadURL();
    // If there is no download url and the key starts with a dash, this could be a legacy AWS file.
    if (!url && key.startsWith('-')) {
      const requestKey = FirebaseWorker.ref(`photoheight/server_requests/sign_files/requests`).push({ name: key, method: 'GET' }).key;
      // Try to get a signed url for the aws file.
      url = await new Promise((resolve) => {
        const callback = FirebaseWorker.ref(`photoheight/server_requests/sign_files/responses/${requestKey}`).on('value', (s) => {
          if (s.exists()) {
            s.ref.remove();
            s.ref.off(callback);
            resolve(s.val());
          }
        });
      });
    }
    // Make sure we have a url.
    if (!url) throw 'Failed to get download url';
    // Fetch the data and convert it to a blob.
    const blob = await fetch(url).then((res) => res.blob());
    // Yeet that blob back to the caller.
    return blob;
  }
  _inputChanged(e) {
    this._addFiles(e.target.files);
    this.$.input.value = null;
  }
  _openFile(e, download) {
    let key = e.target.key;
    return this._getFile(key).then((blob) => {
      // Get file name for download.
      let a = document.createElement('a');
      a.href = window.URL.createObjectURL(blob);
      a.target = '_blank';
      const file = this.$.files.data.find((x) => x.$key == key);
      const fileName = file.name;
      // Set the filename if it is not going to open in Chrome
      if (download === true || !fileName.endsWith('.pdf')) a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
    });
  }
  _or(a, b) {
    return Boolean(a) || Boolean(b);
  }
  async _uploadFile(key) {
    const fileObject = this.get(`_queue.${key}`);
    if (!fileObject) throw 'Could not find file object';

    const metadata = this.uploadAsPublic ? { customMetadata: { public_read: 'true' } } : undefined;
    const uploadTask = firebase.storage().ref(`${this.storagePath}/${key}`).put(fileObject, metadata);
    this.set(`_uploadTasks.${key}`, uploadTask);
    await uploadTask;
    this.dispatchEvent(new CustomEvent('file-uploaded'));
  }
  _showDelete(addOnly, disabled, readOnly) {
    if (readOnly) return false;
    return !addOnly && !disabled;
  }
  _showAddFile(readOnly, disabled) {
    return !readOnly && !disabled;
  }
  async _promptDeleteFile() {
    let messageBody = `Are you sure you want to delete the ${this.file.name} file? You will not be able to recover it once it's deleted.`;

    const dialogConfirm = KatapultDialog.confirm({
      body: messageBody,
      dialog: {
        title: 'Delete File',
        color: 'var(--paper-red-500)'
      }
    });

    const didConfirm = await dialogConfirm.confirmed;

    // If the user canceled, do nothing.
    if (!didConfirm) return;

    this._deleteFile(this.file.$key);
  }
  _updateFiles() {
    const temp = this._files?.filter((x) => x.name != null) ?? [];
    this.files = temp;
  }

  _showOpenInWindow(file) {
    const type = file.type;
    return type ? type === 'application/pdf' : file?.name?.endsWith('.pdf');
  }
}
window.customElements.define(FileInput.is, FileInput);
